'use client'

/* ---------------------------------
Counter
--------------------------------- */

import React, { ReactElement, useEffect, useRef, useState } from 'react'
import Script from 'next/script'
import {
  Card,
  InfoBanner,
  OpeningTitle,
  Paragraph,
  Section,
  Title,
} from '@dynamo-caffeina-dev/airc-ui-kit'
import { SectionBackground } from '@/types'
import DOMPurify from 'isomorphic-dompurify'
import { CounterModel } from '@/models/Counter'
import Spinner from '@/components/atoms/Spinner'
import DonationButton from '@/components/atoms/Buttons/DonationButton'

type OwnProps = {
  props: ReturnType<typeof CounterModel>['props']
  modelledContent: ReturnType<typeof CounterModel>['modelledContent']
}

export default function Counter({
  props,
  modelledContent: extractedContent,
}: OwnProps): ReactElement {
  const {
    openingTitle,
    title: _title,
    paragraph: _paragraph,
    config,
  } = extractedContent ?? {}
  const [loading, setLoading] = useState<boolean>(true)
  const [error, setError] = useState<string>('')
  const [counter, setCounter] = useState<number | null>(null)
  const [libReady, setLibReady] = useState(false)
  const libDidStart = useRef(false)

  const { content: titleContent, ...title } = _title ?? {}
  const { content: paragraphContent, ...paragraph } = _paragraph ?? {}
  const { bg } = props ?? {}

  useEffect(() => {
    if (!config || !libReady || libDidStart.current) return

    const { ObjCounter } = window ?? {}

    if (ObjCounter) {
      try {
        const { serverTimeEndpoint, counterEndpoint } = config

        ObjCounter.init(serverTimeEndpoint, counterEndpoint, (v: number) => {
          setCounter(v)
          setLoading(false)
        })

        ObjCounter.start()
        libDidStart.current = true
      } catch (e: any) {
        setError(e?.message || 'Impossibile inizializzare il contatore')
        setLoading(false)
      }
    }

    return () => {
      try {
        ObjCounter?.stop?.()
      } finally {
        libDidStart.current = false
      }
    }
  }, [config, libReady])

  function formatAmount(value: number, locale: string = 'it-IT'): string {
    if (value >= 1_000_000) {
      return new Intl.NumberFormat(locale, {
        notation: 'compact',
        maximumFractionDigits: 1,
      }).format(value)
    }

    return new Intl.NumberFormat(locale).format(value)
  }

  return (
    <>
      <Section
        bg={bg as SectionBackground | undefined}
        className={`section-counter`}
        // data-container-bg={!!containerBg}
        // data-direction={direction ?? 'row'}
        // data-distribution={distribution ?? 'text-image'}
        // style={
        //   {
        //     '--container-bg': `var(--airc-ui__colors-${containerBg})`,
        //   } as CSSProperties
        // }
      >
        <div className='section-counter__grid'>
          <div className='section-counter__grid__col-1'>
            {/* TITLE */}
            <div className={`section-counter__title-wrapper`}>
              {openingTitle.children ? (
                <OpeningTitle
                  className={`section-testimonial-slider__opening-title`}
                  {...openingTitle}
                />
              ) : null}

              {titleContent ? (
                <Title
                  className={`section-counter__title`}
                  {...title}
                  dangerouslySetInnerHTML={{
                    __html: DOMPurify.sanitize(titleContent),
                  }}
                />
              ) : null}
            </div>

            {/* CONTENT */}
            {paragraphContent ? (
              <Paragraph
                // We override Paragraph's default alignment by following the Title's
                // alignment; this is needed because the WYSIWYG editor won't pass
                // any style for its default alignment (which is left).
                // If the user sets an alignment other than left in the WYSIWYG editor,
                // the resulting HTML with inline styles will override this alignment.
                alignment={title?.alignment}
                className={`section-counter__paragraph`}
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(paragraphContent),
                }}
                {...paragraph}
              />
            ) : null}
          </div>

          <div className='section-counter__grid__col-2'>
            <Card className='section-counter__counter'>
              {loading ? (
                <Spinner />
              ) : error ? (
                <InfoBanner
                  icon='Error'
                  variant='error'
                >
                  {error}
                </InfoBanner>
              ) : (
                <div className='section-counter__counter-wrapper'>
                  <div className='section-counter__counter-indicator'>
                    <span />
                    Donati sino ad ora
                  </div>

                  <div
                    aria-live='polite'
                    className='section-counter__counter-value'
                  >
                    {counter !== null ? (
                      <div className='section-counter__counter-value__inner'>
                        <span>{formatAmount(counter, 'it-IT')}</span>
                        <span> €</span>
                      </div>
                    ) : (
                      '--'
                    )}
                  </div>

                  <DonationButton
                    fullWidth
                    content='Contribuisci'
                    gtmZone={'Counter widget'}
                  />
                </div>
              )}
            </Card>
          </div>
        </div>
      </Section>

      {/* Ref: https://nextjs.org/docs/app/api-reference/components/script */}
      <Script
        id='airc-counter-lib'
        src={process.env.NEXT_PUBLIC_COUNTER_LIB_URL}
        // […] Load the script early but after some hydration on the page occurs.
        strategy='afterInteractive'
        // […] execute code after the script's load event when it first loads
        // and then after every subsequent component re-mount
        onReady={() => setLibReady(true)}
        onError={() => {
          setError('Impossibile caricare la libreria del contatore')
          setLoading(false)
        }}
      />
    </>
  )
}
